import * as React from "react";
import VideoModal from "../components/VideoModal";
import type { ProjectVideoFile } from "../api/models/ApiModels";

/**
 * Shared video-modal state for every report view (run / scout / heal).
 *
 * Owns:
 *  - the {uri, label} state for "which video, if any, is currently open"
 *  - the close handler
 *  - the conditional `<VideoModal>` element to drop into the view tree
 *
 * Callers stay simple:
 *   const { play, playRecording, modal } = useVideoPlayer(projectId);
 *   ...
 *   <button onClick={() => play(uri, "Before fix")} />
 *   <button onClick={() => playRecording(rec, "Watch test")} />
 *   ...
 *   return <div>... {modal}</div>;
 *
 * Passing `projectId` enables per-project persistence of the chosen speed.
 * `play` is a no-op when `uri` is nullish — saves every caller a guard.
 * `playRecording` accepts the ProjectVideoFile shape produced by
 * `mapRecordingsToVideoFiles` / `findRecordingForVideoPath`.
 */
export function useVideoPlayer(projectId?: string) {
    const [playing, setPlaying] = React.useState<{ uri: string; label: string } | null>(null);

    const play = React.useCallback((uri: string | null | undefined, label: string) => {
        if (uri) setPlaying({ uri, label });
    }, []);

    const playRecording = React.useCallback((rec: ProjectVideoFile | undefined | null, label: string) => {
        if (rec?.url) setPlaying({ uri: rec.url, label });
    }, []);

    const close = React.useCallback(() => setPlaying(null), []);

    const modal = playing
        ? <VideoModal uri={playing.uri} label={playing.label} projectId={projectId} onClose={close} />
        : null;

    return { play, playRecording, close, isPlaying: playing !== null, modal };
}
